import {Fragment} from 'react';
import type {ReactNode} from 'react';

export interface TextMatchProps {
  matcher?: string | string[] | RegExp | null;
  referenceText?: string;
  renderMatchedText?: (options: {matchedText: string}) => ReactNode;
  renderUnmatchedText?: (options: {unmatchedText: string}) => ReactNode;
  text: string;
}

const splitterCache: {[key: string]: RegExp | null} = {};

export const TextMatch = ({matcher, renderMatchedText, renderUnmatchedText, text, referenceText}: TextMatchProps) => {
  const splitter = getSplitter(matcher);

  if (text === '' || splitter == null || splitter.source === '()') {
    return <>{text}</>;
  }

  const renderPart = (part: string, index: number, originalPart = part) => {
    splitter.lastIndex = 0;
    const matched = part.match(splitter) != null;
    let content;

    if (matched) {
      if (renderMatchedText != null) {
        content = renderMatchedText({matchedText: originalPart});
      } else {
        content = <mark>{originalPart}</mark>;
      }
    } else {
      if (renderUnmatchedText != null) {
        content = renderUnmatchedText({unmatchedText: originalPart});
      } else {
        content = originalPart;
      }
    }

    return <Fragment key={`${part}-${index}`}>{content}</Fragment>;
  };

  if (referenceText != null) {
    let currentPartStart = 0;

    return referenceText
      .split(splitter)
      .filter((part) => part !== '')
      .map((part, index) => {
        const originalPart = text.slice(currentPartStart, currentPartStart + part.length);
        currentPartStart = currentPartStart + part.length;
        return renderPart(part, index, originalPart);
      });
  }

  return text
    .split(splitter)
    .filter((part) => part !== '')
    .map((part, index) => renderPart(part, index));
};

function getSplitter(matcher: string | string[] | RegExp | null = null) {
  const splitterCacheKey = getSplitterCacheKey(matcher);

  if (splitterCacheKey in splitterCache) {
    return splitterCache[splitterCacheKey];
  }

  let splitter: RegExp | null;

  if (matcher == null) {
    splitter = null;
  } else if (typeof matcher === 'string') {
    splitter = new RegExp(`(${escape(matcher)})`, 'gi');
  } else if (Array.isArray(matcher)) {
    splitter = new RegExp(`(${matcher.map(escape).join('|')})`, 'gi');
  } else {
    splitter = new RegExp(`(${matcher.source})`, matcher.flags);
  }

  splitterCache[splitterCacheKey] = splitter;

  return splitter;
}

function getSplitterCacheKey(matcher: string | string[] | RegExp | null = null) {
  if (matcher == null) {
    return 'N';
  } else if (typeof matcher === 'string') {
    return `S:${matcher}`;
  } else if (Array.isArray(matcher)) {
    return `A:${matcher.join('|')}`;
  } else {
    return `R:${matcher}`;
  }
}

function escape(value: string) {
  return value.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
}
